<script setup lang="ts">
import { reactive, ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import { DEFAULT_LOCALE, type Locale } from '@/i18n'
import { staticPageHref, accountHref } from '@/router/products'
import { useAuth } from '@/composables/useAuth'
import { useContracts } from '@/composables/useContracts'
import { useBank } from '@/composables/useBank'
import { normalizeBank, validateIban, validateBic, type IbanError, type BicError } from '@/utils/bank'
import { ApiError } from '@/services/http'
import AccountLayout from '@/components/layout/AccountLayout.vue'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { member, contractsLoading } = useAuth()
const { hasFsmeCard, fsmeContract } = useContracts()
const { bank, fullIban, fullBic, revealError, reveal, saveBank, createBank } = useBank()

const currentLocale = computed<Locale>(() => (route.meta.locale as Locale) ?? DEFAULT_LOCALE)
const loginHref = computed(() => staticPageHref('login', currentLocale.value))
const dashboardHref = computed(() => accountHref('dashboard', currentLocale.value))
const contactHref = computed(() => accountHref('contact', currentLocale.value))
useHead({ title: computed(() => `${t('auth.account.payment.navTitle')} — MySmerra`) })

/** Accès réservé. */
onMounted(() => {
  if (!member.value) router.replace(loginHref.value)
})

// Le RIB de remboursement n'existe que pour un adhérent éligible FSME. Les
// contrats arrivent de façon asynchrone (`GET /contracts`) : on n'arbitre qu'une
// fois le chargement terminé, sinon un simple F5 éjecterait un adhérent légitime
// dont les contrats ne sont pas encore là. Non éligible → retour au dashboard.
watch(
  [member, contractsLoading, hasFsmeCard],
  ([currentMember, loading, eligible]) => {
    if (!currentMember || loading) return
    if (!eligible) router.replace(dashboardHref.value)
  },
  { immediate: true },
)

/** Tant que l'éligibilité n'est pas tranchée (contrats en vol), on n'affiche
 *  rien : évite un flash de la page avant une éventuelle redirection. */
const canShow = computed(() => !!member.value && !contractsLoading.value && hasFsmeCard.value)

// IBAN + BIC masqués par défaut ; à la révélation, on récupère les valeurs complètes.
const showBank = ref(false)
async function toggleBank() {
  if (!showBank.value) await reveal()
  showBank.value = !showBank.value
}
const ibanLabel = computed(() => {
  if (!bank.value) return ''
  return showBank.value && fullIban.value ? fullIban.value : bank.value.maskedIban
})
const bicLabel = computed(() => {
  if (!bank.value) return ''
  return showBank.value && fullBic.value ? fullBic.value : bank.value.maskedBic
})

/* --- Enregistrement du RIB de remboursement : création (POST …/banking_account_detail)
 *     quand aucun RIB n'existe, sinon modification (PATCH …/:id). --- */
const editing = ref(false)
const saving = ref(false)
const done = ref(false)
const submitted = ref(false)
const saveError = ref<string | null>(null)
const form = reactive({ iban: '', bic: '', holder: '' })

/** Vrai quand il n'existe pas encore de RIB : le formulaire crée le premier. */
const isCreate = computed(() => !bank.value)
/** Mémorise si le dernier enregistrement était une création (titre de succès dédié). */
const justCreated = ref(false)

/** Codes d'erreur IBAN/BIC (format + clé MOD-97 + zone SEPA, cf. @/utils/bank) → clés i18n. */
const IBAN_ERROR_KEYS: Record<IbanError, string> = {
  required: 'auth.account.payment.requiredError',
  format: 'auth.account.payment.ibanInvalid',
  checksum: 'auth.account.payment.ibanChecksum',
  sepa: 'auth.account.payment.ibanSepa',
}
const BIC_ERROR_KEYS: Record<BicError, string> = {
  required: 'auth.account.payment.requiredError',
  format: 'auth.account.payment.bicInvalid',
}
const errors = computed(() => {
  const ibanCode = validateIban(form.iban)
  const bicCode = validateBic(form.bic)
  return {
    iban: ibanCode ? t(IBAN_ERROR_KEYS[ibanCode]) : '',
    bic: bicCode ? t(BIC_ERROR_KEYS[bicCode]) : '',
    holder: form.holder.trim() ? '' : t('auth.account.payment.requiredError'),
  }
})
const showErr = (key: 'iban' | 'bic' | 'holder') => submitted.value && !!errors.value[key]

/** Ouvre le formulaire (création ou modification) avec des champs vierges : par
 *  sécurité, l'IBAN/BIC/titulaire ne sont jamais pré-remplis — l'adhérent doit
 *  les (re)saisir intégralement. */
function startEdit() {
  saveError.value = null
  submitted.value = false
  done.value = false
  form.iban = ''
  form.bic = ''
  form.holder = ''
  editing.value = true
}
function cancelEdit() {
  editing.value = false
}
async function submitEdit() {
  submitted.value = true
  if (errors.value.iban || errors.value.bic || errors.value.holder) return
  saving.value = true
  saveError.value = null
  const creating = isCreate.value
  try {
    const values = {
      iban: normalizeBank(form.iban),
      bic: normalizeBank(form.bic),
      ownerFullname: form.holder.trim(),
    }
    if (creating) {
      // Cible : le contrat actif remonté à FSME (celui qui rend l'adhérent éligible).
      if (!fsmeContract.value) throw new Error('no eligible contract')
      await createBank(fsmeContract.value.id, values)
    } else {
      await saveBank({ ...values, bankName: bank.value?.bankName ?? undefined })
    }
    justCreated.value = creating
    editing.value = false
    done.value = true
  } catch (err) {
    saveError.value =
      err instanceof ApiError && err.status === 422
        ? t('auth.account.payment.invalidError')
        : t('auth.account.payment.saveError')
  } finally {
    saving.value = false
  }
}

// Ouverture automatique du formulaire une fois les contrats chargés :
//  - depuis le dashboard via ?edit=rib (modification ou création selon l'existant) ;
//  - adhérent éligible sans RIB : formulaire de création ouvert d'emblée pour
//    permettre la saisie d'un premier RIB.
// Ne s'exécute qu'une fois, et seulement pour un adhérent éligible.
const autoOpened = ref(false)
watch(
  [contractsLoading, bank, hasFsmeCard],
  ([loading, currentBank, eligible]) => {
    if (autoOpened.value || loading || !eligible) return
    if (route.query.edit === 'rib' || !currentBank) {
      autoOpened.value = true
      startEdit()
    }
  },
  { immediate: true },
)
</script>

<template>
  <AccountLayout v-if="canShow && member" active="payment">
    <section class="pay">
      <!-- Fil d'ariane + titre -->
      <nav class="pay__crumb" aria-label="breadcrumb">
        <span>{{ t('auth.account.payment.breadcrumbHome') }}</span>
        <span class="pay__crumb-sep" aria-hidden="true">›</span>
        <span class="pay__crumb-current">{{ t('auth.account.payment.navTitle') }}</span>
      </nav>
      <header class="pay__head">
        <h1 class="pay__title">{{ t('auth.account.payment.navTitle') }}</h1>
        <p class="pay__subtitle">{{ t('auth.account.payment.subtitle') }}</p>
      </header>

      <div class="pay__grid">
        <!-- Colonne principale -->
        <div class="pay__main">
          <p class="pay__section-desc">{{ t('auth.account.bank.intro') }}</p>

          <!-- RIB de remboursement : confirmation / formulaire (création ou
               modification) / affichage / état vide. -->
          <!-- Confirmation après enregistrement -->
          <div v-if="done" class="rib-success">
            <div class="rib-success__head">
              <span class="rib-success__icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="24" height="24">
                  <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.7"/>
                  <path d="M8 12.5l2.5 2.5L16 9.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
              <h3 class="rib-success__title">{{ t(justCreated ? 'auth.account.payment.createSuccessTitle' : 'auth.account.payment.successTitle') }}</h3>
            </div>
            <p class="rib-success__desc">{{ t('auth.account.payment.success') }}</p>
            <button type="button" class="btn-dark" @click="done = false">{{ t('auth.account.payment.successClose') }}</button>
          </div>

          <!-- Formulaire du RIB (création ou modification) -->
          <form v-else-if="editing" class="rib" novalidate @submit.prevent="submitEdit">
              <div class="rib__field">
                <label class="rib__label" for="rib-iban">{{ t('auth.account.payment.ibanLabel') }} <span class="rib__req">*</span></label>
                <input id="rib-iban" v-model="form.iban" class="rib__input" :class="{ 'is-invalid': showErr('iban') }" type="text" autocomplete="off" spellcheck="false" />
                <p v-if="showErr('iban')" class="rib__error">{{ errors.iban }}</p>
                <p v-else class="rib__hint">{{ t('auth.account.payment.ibanExample') }}</p>
              </div>
              <div class="rib__field">
                <label class="rib__label" for="rib-bic">{{ t('auth.account.payment.bicLabel') }} <span class="rib__req">*</span></label>
                <input id="rib-bic" v-model="form.bic" class="rib__input" :class="{ 'is-invalid': showErr('bic') }" type="text" autocomplete="off" spellcheck="false" />
                <p v-if="showErr('bic')" class="rib__error">{{ errors.bic }}</p>
                <p v-else class="rib__hint">{{ t('auth.account.payment.bicExample') }}</p>
              </div>
              <div class="rib__field">
                <label class="rib__label" for="rib-holder">{{ t('auth.account.payment.holderLabel') }} <span class="rib__req">*</span></label>
                <input id="rib-holder" v-model="form.holder" class="rib__input" :class="{ 'is-invalid': showErr('holder') }" type="text" autocomplete="name" />
                <p v-if="showErr('holder')" class="rib__error">{{ errors.holder }}</p>
              </div>
              <p v-if="saveError" class="rib__error">{{ saveError }}</p>
              <p class="rib__note">{{ t('auth.account.payment.requiredNote') }}</p>
              <div class="rib__actions">
                <button type="submit" class="btn-dark" :disabled="saving">{{ saving ? t('auth.account.payment.saving') : t('auth.account.payment.save') }}</button>
                <button type="button" class="btn-ghost" @click="cancelEdit">{{ t('auth.account.payment.cancel') }}</button>
              </div>
            </form>

          <!-- RIB existant : affichage masqué + révélation -->
          <template v-else-if="bank">
              <div class="bank">
                <div class="bank__text">
                  <span class="bank__holder">{{ bank.holder }}</span>
                  <span class="bank__iban">{{ ibanLabel }}</span>
                  <span class="bank__bic">{{ bicLabel }}</span>
                </div>
                <button
                  type="button"
                  class="bank__reveal"
                  :aria-label="showBank ? t('auth.account.bank.hideDetails') : t('auth.account.bank.showDetails')"
                  :aria-pressed="showBank"
                  @click="toggleBank"
                >
                  <svg v-if="showBank" viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
                    <circle cx="12" cy="12" r="3" fill="none" stroke="currentColor" stroke-width="1.8"/>
                  </svg>
                  <svg v-else viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                    <path d="M2 12s3.5-7 10-7c2 0 3.8.66 5.3 1.6M22 12s-3.5 7-10 7c-2 0-3.8-.66-5.3-1.6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                    <path d="M4 4l16 16" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                  </svg>
                </button>
              </div>
              <p v-if="revealError" class="rib__error">{{ t('auth.account.bank.revealError') }}</p>
              <button type="button" class="btn-dark bank__edit" @click="startEdit">
                {{ t('auth.account.bank.edit') }}
              </button>
          </template>

          <!-- Aucun RIB (adhérent éligible) : enregistrer le premier RIB. -->
          <template v-else>
            <p class="pay__empty">{{ t('auth.account.bank.empty') }}</p>
            <button type="button" class="btn-dark bank__edit" @click="startEdit">{{ t('auth.account.bank.create') }}</button>
          </template>
        </div>

        <!-- Colonne latérale -->
        <aside class="pay__aside">
          <article class="aside-card">
            <span class="aside-card__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="22" height="22">
                <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="1.6"/>
                <path d="M9.5 9.5a2.5 2.5 0 1 1 3.6 2.2c-.7.4-1.1.9-1.1 1.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                <circle cx="12" cy="16.5" r="1" fill="currentColor"/>
              </svg>
            </span>
            <h2 class="aside-card__title">{{ t('auth.account.payment.helpTitle') }}</h2>
            <p class="aside-card__desc">{{ t('auth.account.payment.helpDesc') }}</p>
            <RouterLink class="btn-dark aside-card__cta" :to="contactHref">{{ t('auth.account.payment.helpCta') }}</RouterLink>
          </article>
        </aside>
      </div>
    </section>
  </AccountLayout>
</template>

<style scoped>
.pay { display: flex; flex-direction: column; gap: 1rem; }

/* Fil d'ariane + titre */
.pay__crumb { display: flex; align-items: center; gap: 0.4rem; font-size: var(--text-sm); color: var(--text-muted); }
.pay__crumb-current { color: var(--color-chocolate); font-weight: 600; }
.pay__title { font-size: var(--text-xl); font-weight: 700; color: var(--color-chocolate); }
.pay__subtitle { margin-top: 0.15rem; font-size: var(--text-sm); color: var(--text-muted); }

/* Grille principale / latérale */
.pay__grid {
  display: grid;
  grid-template-columns: 1.7fr 1fr;
  gap: 1.25rem;
  align-items: start;
  margin-top: 0.25rem;
}
.pay__main {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.pay__aside { display: flex; flex-direction: column; gap: 1.25rem; }
.pay__section-desc { font-size: var(--text-sm); color: var(--text-muted); }
.pay__empty { margin-top: 0.4rem; font-size: var(--text-sm); color: var(--text-muted); }

/* Coordonnées bancaires — encart, œil à droite */
.bank {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-top: 0.3rem;
  padding: 1rem 1.1rem;
  background: color-mix(in oklab, var(--color-peach) 28%, white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
}
.bank__text { display: flex; flex-direction: column; gap: 0.25rem; min-width: 0; }
.bank__holder { font-size: var(--text-sm); font-weight: 700; color: var(--color-chocolate); }
.bank__iban { font-size: var(--text-sm); color: var(--color-chocolate); letter-spacing: 0.08em; word-break: break-all; }
.bank__bic { font-size: var(--text-sm); color: var(--color-chocolate); letter-spacing: 0.08em; word-break: break-all; }
.bank__reveal {
  flex-shrink: 0;
  display: grid;
  place-items: center;
  padding: 0.3rem;
  border: 0;
  background: none;
  color: var(--text-muted);
  cursor: pointer;
}
.bank__reveal:hover { color: var(--color-chocolate); }

.link-edit {
  align-self: flex-start;
  margin-top: 0.3rem;
  padding: 0;
  border: 0;
  background: none;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
  text-decoration: underline;
  text-underline-offset: 0.18em;
  cursor: pointer;
}
.link-edit:hover { color: var(--color-cherry); }

/* Formulaire RIB */
.rib { display: flex; flex-direction: column; gap: 0.8rem; margin-top: 0.3rem; }
.rib__field { display: flex; flex-direction: column; gap: 0.35rem; }
.rib__label { font-size: var(--text-xs); font-weight: 600; color: var(--color-chocolate); }
.rib__req { color: var(--color-cherry); }
.rib__input {
  width: 100%;
  padding: 0.65rem 0.8rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  background: var(--color-white);
  font: inherit;
  font-size: var(--text-sm);
  color: var(--color-chocolate);
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.rib__input:focus { outline: none; border-color: var(--primary); box-shadow: 0 0 0 3px var(--focus-ring); }
.rib__input.is-invalid { border-color: var(--color-cherry); box-shadow: 0 0 0 3px color-mix(in oklab, var(--color-cherry) 22%, transparent); }
.rib__error { font-size: var(--text-xs); font-weight: 600; color: var(--color-cherry); }
.rib__hint { font-size: var(--text-xs); color: var(--text-muted); }
.rib__note { font-size: var(--text-xs); color: var(--text-muted); }
.rib__actions { display: flex; flex-wrap: wrap; gap: 0.6rem; margin-top: 0.2rem; }
.btn-ghost {
  padding: 0.7rem 1.4rem;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-pill);
  background: var(--color-white);
  color: var(--color-chocolate);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  transition: border-color var(--dur-fast) var(--ease-out);
}
.btn-ghost:hover { border-color: var(--primary); }

/* Confirmation après enregistrement */
.rib-success {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.5rem;
  margin-top: 0.3rem;
  padding: 1.1rem;
  border: 1px solid color-mix(in oklab, #1a7f45 30%, white);
  border-radius: var(--radius-lg);
  background: color-mix(in oklab, #1a7f45 7%, white);
}
.rib-success__head { display: flex; align-items: center; gap: 0.6rem; }
.rib-success__icon { display: grid; place-items: center; color: #1a7f45; flex-shrink: 0; }
.rib-success__title { font-size: var(--text-base); font-weight: 700; color: var(--color-chocolate); }
.rib-success__desc { font-size: var(--text-sm); color: var(--text-muted); line-height: var(--leading-normal); }
.rib-success .btn-dark { margin-top: 0.3rem; }

/* Carte latérale */
.aside-card {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-start;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-sm);
  padding: 1.25rem;
}
.aside-card__icon {
  display: grid;
  place-items: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: var(--radius-lg);
  color: var(--primary);
  background: color-mix(in oklab, var(--primary) 12%, white);
}
.aside-card__title { font-size: var(--text-base); font-weight: 700; color: var(--color-chocolate); }
.aside-card__desc { font-size: var(--text-xs); color: var(--text-muted); line-height: var(--leading-normal); }
.aside-card__cta { margin-top: 0.2rem; text-decoration: none; }

/* Bouton sombre */
.btn-dark {
  padding: 0.7rem 1.4rem;
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--color-chocolate);
  color: var(--color-white);
  font-size: var(--text-sm);
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background var(--dur-fast) var(--ease-out);
}
.btn-dark:hover { background: color-mix(in oklab, var(--color-chocolate) 85%, white); }
.btn-dark:disabled { opacity: 0.5; cursor: default; }
.btn-dark:disabled:hover { background: var(--color-chocolate); }
.bank__edit { align-self: flex-start; margin-top: 0.6rem; }

@media (max-width: 64rem) {
  .pay__grid { grid-template-columns: 1fr; }
}
</style>
